import { Component, HostListener, OnInit } from "@angular/core";
import { NavigationEnd, NavigationError, Router } from "@angular/router";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import {
  AlertController,
  MenuController,
  ModalController,
  Platform,
} from "@ionic/angular";
import { NgxPermissionsService } from "ngx-permissions";
import { Observable, Subject, Subscription, lastValueFrom } from "rxjs";
import { filter } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { register } from "swiper/element/bundle";
import { bootScreenUntil } from "./boot-state.util";
import { BiometricService } from "./services/biometric.service";
import { GeolocationService } from "./services/geolocation.service";
import { MinAppVersionService } from "./services/min-app-version.service";
import { DataService } from "./services/preload/data.service";
import { AuditTrailService } from "./services/swagger";
import { UserDataService } from "./services/user/user.service";
import { ROUTES } from "./utils/routes";

// Swiper's register function to globally register Swiper's custom elements.
register();

@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
  styleUrls: ["app.component.scss"],
})
export class AppComponent implements OnInit {
  userActivity: any;
  userInactive: Subject<any> = new Subject();
  checkInActivity: boolean = true;
  _subscription: Subscription;
  canShowSidebar: boolean;
  currentUser: any;
  showLockScreen: boolean = false;
  authError: string = "";
  isPublicRoute: boolean = false;
  // Keeps the boot screen from index.html up until the first page is on
  // screen (either router outlet reporting its first stack change).
  firstPage$ = new Subject<void>();
  booting$: Observable<boolean>;

  @HostListener("window:mousemove") refreshUserState() {
    clearTimeout(this.userActivity);
    this.setTimeout();
  }

  constructor(
    private bioService: BiometricService,
    private geolocationService: GeolocationService,
    private platform: Platform,
    private menuController: MenuController,
    private auditTrailService: AuditTrailService,
    private ngxPermissionsService: NgxPermissionsService,
    private dataService: DataService,
    private router: Router,
    private alertCtrl: AlertController,
    private modalController: ModalController,
    public userService: UserDataService,
    private minAppVersionService: MinAppVersionService,
  ) {
    this.booting$ = bootScreenUntil(
      this.firstPage$,
      this.router.events.pipe(filter((e) => e instanceof NavigationError))
    );
    if (Capacitor.getPlatform() === "android") {
      document.documentElement.style.setProperty("--ion-safe-area-top", "14px");
    }
    this.initializeApp();
    this.setTimeout();
    this.userInactive.subscribe(async (v) => {
      let currentUser = await this.userService.getCurrentUser();
      let multiTenants = await this.userService.getMultiTenants();
      if (
        (this.checkInActivity && currentUser) ||
        (this.checkInActivity && multiTenants)
      ) {
        await lastValueFrom(
          this.auditTrailService.apiAudittrailInsertPost({
            auditTrailEventInsertModel: {
              auditTrailActionId: 2,
              auditTrailTypeId: 1,
              auditTrailNotes: `User ${currentUser?.userName} has been logout for inactivity from account name ${currentUser.companyName}`,
            },
          }),
        );
        const isModalOpened = await this.modalController.getTop();
        if (isModalOpened) {
          await this.modalController.dismiss();
        }
        await this.userService.logout();
        this.checkInActivity = false;
        this.alertDeleteError();
      }
    });
  }

  async ngOnInit() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.isPublicRoute = event.urlAfterRedirects.startsWith("/dashboard/");
      }
    });
    this.isPublicRoute = this.router.url.startsWith("/dashboard/");

    this._subscription = this.userService.permissions$.subscribe(
      (permissions: any[]) => {
        if (permissions) {
          this.ngxPermissionsService.loadPermissions(permissions);
        } else {
          this.ngxPermissionsService.flushPermissions();
        }
      },
    );
    this._subscription.add(
      this.userService.userSettings$.subscribe((settings) => {
        const hideSideMenuSetting = settings?.find(
          (s) => s.userSettingName === "HideSideMenu",
        );

        if (hideSideMenuSetting?.userSettingValue === "Yes") {
          this.canShowSidebar = false;
        } else {
          this.canShowSidebar = true;
        }
      }),
    );
    this._subscription.add(
      this.userService.user$.subscribe(async (user) => {
        this.currentUser = user;
        if (user) {
          // Load independently so a failure in one endpoint doesn't abort the
          // others (otherwise gated cards stay stuck on the "loading..." state).
          await Promise.allSettled([
            this.dataService.loadAllCustomers(),
            this.dataService.getAllIUsersList(),
            this.dataService.getAllItemsList(),
            this.dataService.getDropdownItemsList(),
          ]);
          this.minAppVersionService.check();
        }
      }),
    );

    if (Capacitor.getPlatform() !== "web") {
      App.addListener("appStateChange", async ({ isActive }) => {
        if (isActive && this.currentUser) {
          this.minAppVersionService.check();
        }
      });
    }
    this._subscription.add(
      this.bioService.biometricChange$.subscribe((value) => {
        if (value) {
          this.initializeBioAuth();
        }
      }),
    );
  }

  initializeApp() {
    let splashMinDuration = new Promise((resolve) => setTimeout(resolve, 3000));
    this.platform.ready().then(() => {
      if (!window.location.pathname.startsWith("/dashboard/")) {
        this.geolocationService.requestPermissionOnAppStart();
      }
      if (this.platform.is("capacitor")) {
        splashMinDuration.then(() => SplashScreen.hide());
        this.initializeBioAuth();
      }
    });
  }

  ngOnDestroy() {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
  }

  setTimeout() {
    this.userActivity = setTimeout(
      () => this.userInactive.next(undefined),
      environment.inActivityTime,
    );
  }

  hasClass(element, className) {
    return (" " + element.className + " ").indexOf(" " + className + " ") > -1;
  }

  async alertDeleteError() {
    let alertView = await this.alertCtrl.create({
      header: "Stay connected!",
      message: "Your session has been logged out due to inactivity.",
      buttons: [
        {
          text: "Continue",
          role: "confirm",
          cssClass: "confirm-btn",
        },
      ],
      mode: "md",
      backdropDismiss: false,
      cssClass: "delete-alert",
    });
    alertView.present();
  }

  closeMenu() {
    this.menuController.close();
  }

  async initializeBioAuth() {
    const isApp = Capacitor.getPlatform() !== "web";
    const currentUser = await this.userService.getCurrentUser();
    const userPermissions = await this.userService.getUserPermissions();
    const settings = await this.userService.getCurrentUserSettings();
    let allowBioAuthForUser: boolean = false;

    if (settings && settings.length) {
      allowBioAuthForUser = settings.find(
        (setting) =>
          setting.userSettingName === "BiometricAuthentication" &&
          setting.userSettingType === "Profile" &&
          setting.userSettingValue === "Yes",
      )
        ? true
        : false;
    }

    if (!isApp || !currentUser || !userPermissions || !allowBioAuthForUser) {
      return;
    }

    const available = await this.bioService.isAvailable();

    if (available && !available.isAvailable && available.errorCode === 4) {
      this.showLockScreen = true;
      this.authError = `Biometric verification is temporarily locked due to too many failed attempts. Please try again in about a minute.`;
      return;
    }

    if (available && !available.isAvailable && available.errorCode !== 4) {
      this.showLockScreen = false;
      this.authError = "Biometric authentication not available";
      return;
    }

    this.showLockScreen = true;
    this.authError = "";

    try {
      const success = await this.bioService.authenticate({
        reason: "Authenticate to continue", // optional message on iOS
        title: "Biometric Authentication",
        subtitle: "Use Face ID or Fingerprint to unlock",
        description: "Place your finger on the sensor or look at your device",
        fallbackTitle: "Use PIN", // optional button for fallback
        maxAttempts: 10,
      });

      if (success) {
        this.showLockScreen = false;
        this.authError = "";
      } else {
        this.authError = "Authentication failed. Tap fingerprint to try again.";
      }
    } catch (err: any) {
      console.error("Auth error", err);

      // Handle max failed attempts / locked out
      if (err === "User Lockout" || err.code === "LOCKED_OUT") {
        this.authError =
          "Too many failed attempts. Please use device PIN to unlock.";
      } else {
        this.authError = "Authentication failed. Tap fingerprint to try again.";
      }
    }
  }
}
